// A header cell's resize boundary: a vertical separator at the cell's trailing edge, named for its column
// and carrying its bounds and current width. It is absent below 768px and on columns that do not resize.
//
// The header cell renders the boundary beside its sort control, never inside it. The colgroup owns widths;
// while a drag runs the boundary writes the live width onto its column's <col> and header cell, and when the
// drag ends the colgroup's committed width takes over again.

import React, { useCallback, useLayoutEffect, useRef } from 'react'
import PropTypes from 'prop-types'

import { useTable } from '../context'
import { ACTIONS } from '../types'
import { resizeLabel, widthText } from './copy'
import { useResize } from './useResize'
import {
  previewWidths,
  resizeFloor,
  shownWidth,
  tableMinWidth,
  widthsForResize,
} from '../geometry/ColGroup'
import { fitWidth } from '../geometry/contentFloor'
import { effectiveMin, isResizable } from './view'

const escapeKey = key =>
  typeof CSS !== 'undefined' && CSS.escape
    ? CSS.escape(key)
    : String(key).replace(/["\\]/g, '\\$&')

// a drag previews on the <col> and the header cell; at rest the <col> holds the committed width and the
// header cell carries none, so a cancelled drag restores the committed width
const applyWidth = (cell, key, widths, live, tableMinimum) => {
  if (!cell) return
  const px = `${widths.find(item => item.key === key).width}px`
  const unit = cell.closest('.me-table-unit')
  const tables = unit ? Array.from(unit.querySelectorAll('table.me-table')) : []
  tables
    .filter(table => table.closest('.me-table-unit') === unit)
    .forEach(table => {
      const cols = table.querySelectorAll(':scope > colgroup > col')
      if (cols.length !== widths.length) return
      widths.forEach((item, index) => {
        cols[index].style.width = `${item.width}px`
      })
      const total = widths.reduce((sum, item) => sum + item.width, 0)
      table.style.width = `${total}px`
      table.style.minWidth = `${live === null ? tableMinimum : total}px`
    })
  if (live !== null) cell.style.width = px
  else cell.style.removeProperty('width')
}

// each boundary's live handlers, by its element, so the leading half of its touch grab area (Lead) can
// start the same drag
const HANDLERS = new WeakMap()

const Boundary = ({
  column,
  width,
  floor,
  min,
  max,
  commit,
  onPreview,
  renderWidths,
  tableMinimum,
}) => {
  const node = useRef(null)
  const previewed = useRef(false)
  const lastCell = useRef(null)
  const latest = useRef(null)
  latest.current = { renderWidths, tableMinimum }
  const fit = useCallback(
    () => fitWidth(node.current && node.current.closest('th'), column.key),
    [column.key]
  )
  const { live, handlers } = useResize({
    column,
    width,
    floor,
    commit,
    onPreview,
    fit,
  })
  const current = useRef(handlers)
  current.current = handlers
  const shown = live === null ? width : live

  useLayoutEffect(() => {
    const element = node.current
    if (!element) return undefined
    HANDLERS.set(element, current)
    return () => HANDLERS.delete(element)
  }, [])

  useLayoutEffect(() => {
    // React owns the idle layout. Only the active drag and its cleanup need an imperative writer.
    if (live === null && !previewed.current) return
    const cell = node.current && node.current.closest('th')
    lastCell.current = cell
    applyWidth(cell, column.key, renderWidths(live), live, tableMinimum)
    previewed.current = live !== null
  }, [column.key, renderWidths, live, tableMinimum])

  useLayoutEffect(
    () => () => {
      if (previewed.current) {
        const current = latest.current
        applyWidth(
          lastCell.current,
          column.key,
          current.renderWidths(null),
          null,
          current.tableMinimum
        )
      }
    },
    [column.key]
  )

  return (
    <div
      ref={node}
      role="separator"
      aria-orientation="vertical"
      aria-label={resizeLabel(column)}
      aria-valuemin={min}
      aria-valuemax={max}
      aria-valuenow={shown}
      aria-valuetext={widthText(shown)}
      tabIndex={0}
      className={`me-table-resize${live === null ? '' : ' is-dragging'}`}
      data-column-key={column.key}
      {...handlers}
    />
  )
}

Boundary.propTypes = {
  column: PropTypes.object.isRequired,
  width: PropTypes.number.isRequired,
  floor: PropTypes.number,
  min: PropTypes.number,
  max: PropTypes.number,
  commit: PropTypes.func.isRequired,
  onPreview: PropTypes.func.isRequired,
  renderWidths: PropTypes.func.isRequired,
  tableMinimum: PropTypes.number.isRequired,
}

/** Whether a column renders a resize boundary at the current breakpoint. */
export const hasBoundary = (column, layout) =>
  !(layout && layout.narrow) && isResizable(column)

// the named column's boundary, in the same header row
const boundaryFor = (element, key) => {
  const row = element && element.closest('tr')
  return row
    ? row.querySelector(`.me-table-resize[data-column-key="${escapeKey(key)}"]`)
    : null
}

// The leading half of a column's hit target. The target is centred on the column's trailing edge (12px,
// or 24px on a touch screen), and the next header cell clips its own content, so the half past the edge is
// rendered inside that cell, with the half of the boundary line on its side. A press on it starts the
// boundary's own drag (the boundary then captures the pointer). It is not a second control: no role and
// not focusable, so assistive technology meets the one separator.
const Lead = ({ column }) => {
  const forward = name => event => {
    const boundary = boundaryFor(event.currentTarget, column.key)
    const entry = boundary && HANDLERS.get(boundary)
    const handler = entry && entry.current[name]
    if (typeof handler !== 'function') return
    handler({
      button: event.button,
      pointerId: event.pointerId,
      clientX: event.clientX,
      currentTarget: boundary,
      preventDefault: () => event.preventDefault(),
      stopPropagation: () => event.stopPropagation(),
    })
  }
  return (
    <div
      className="me-table-resize-lead"
      aria-hidden="true"
      data-column-key={column.key}
      onPointerDown={forward('onPointerDown')}
      onDoubleClick={forward('onDoubleClick')}
      onClick={event => event.stopPropagation()}
    />
  )
}

Lead.propTypes = {
  column: PropTypes.object.isRequired,
}

/**
 * A column's resize boundary, or with lead the leading half of its touch grab area, which the next header
 * cell renders (HeaderRow.js).
 */
export const ResizeBoundary = ({ column, lead = false }) => {
  const table = useTable()
  const { dispatch, layout, geometry } = table
  const enabled = hasBoundary(column, layout)
  const invalidate = geometry && geometry.invalidate
  const onPreview = useCallback(() => {
    if (typeof invalidate === 'function') invalidate()
  }, [invalidate])

  // the boundary starts from the rendered width: a content floor may hold it above the saved one, and spare
  // width may widen a truncated column at its configured width; a drag or key press starts from what shows
  const floor = resizeFloor(table, column)
  const width = shownWidth(table, column)
  const renderWidths = useCallback(
    next => previewWidths(table, column, next),
    [table, column]
  )
  const commit = useCallback(
    next => {
      dispatch({
        type: ACTIONS.viewSet,
        view: { widths: widthsForResize(table, column, next) },
        reason: 'view',
      })
      onPreview()
    },
    [dispatch, table, column, onPreview]
  )

  if (!enabled || typeof width !== 'number') return null
  if (lead) return <Lead column={column} />
  const { max } = column.size || {}
  return (
    <Boundary
      column={column}
      width={width}
      floor={floor}
      min={effectiveMin(column, floor)}
      max={Math.max(max || 0, effectiveMin(column, floor))}
      commit={commit}
      onPreview={onPreview}
      renderWidths={renderWidths}
      tableMinimum={tableMinWidth(table)}
    />
  )
}

ResizeBoundary.propTypes = {
  column: PropTypes.object.isRequired,
  lead: PropTypes.bool,
}
